import {
  Building2,
  CalendarCheck,
  CreditCard,
  Mail,
  Rocket,
  ShieldCheck,
  Smartphone,
  TriangleAlert,
  UserCog,
  Users,
  Wrench,
} from 'lucide-react';
import type { Section } from './ChecklistBoard';

export const UAT_SECTIONS: Section[] = [
  {
    id: 'auth',
    title: 'Authentication & Access Roles',
    description: 'Sign in/out, password reset, and that each role only sees what it should.',
    icon: ShieldCheck,
    scenarios: [
      { id: 'auth-signin', title: 'Owner can sign in with email + password', detail: 'Valid credentials land on /admin; invalid credentials show a clear error and do not sign in.' },
      { id: 'auth-signout', title: 'Sign out clears the session', detail: 'After signing out the user is returned to /auth and protected pages redirect away.' },
      { id: 'auth-reset', title: 'Password reset flow works end-to-end', detail: 'Reset email arrives, the link opens /reset-password, and the new password works on next sign in.' },
      { id: 'auth-stale', title: 'Stale / expired session is handled gracefully', detail: 'An expired session prompts re-auth rather than rendering a broken admin shell.' },
      { id: 'auth-platform-admin', title: 'Platform admin can reach /platform', detail: 'A super_admin sees the Platform Admin area; a normal owner is redirected to "/".' },
      { id: 'auth-owner-scope', title: 'Org owner cannot reach /platform', detail: 'Navigating to /platform as an org owner redirects home — no founder tools leak.' },
      { id: 'auth-cleaner-scope', title: 'Cleaner role sees only their calendar', detail: 'A cleaner/team member sees the restricted nav, not company/billing/website settings.' },
      { id: 'auth-guarded-routes', title: 'Unauthenticated access to /admin and /platform is blocked', detail: 'Direct URL entry while signed out never renders protected content.' },
    ],
  },
  {
    id: 'onboarding',
    title: 'Business Onboarding',
    description: 'New business signup through to a usable, branded tenant.',
    icon: Building2,
    scenarios: [
      { id: 'onboard-signup', title: 'Business signup wizard completes', detail: 'New business can register via /signup and reaches confirmation without errors.' },
      { id: 'onboard-confirm', title: 'Signup confirmation email is received', detail: 'Confirmation/magic link arrives and confirming activates the account.' },
      { id: 'onboard-resend', title: 'Confirmation can be resent', detail: 'Resend control works and does not duplicate the account.' },
      { id: 'onboard-team', title: 'Team setup invites work', detail: 'Owner can invite team members; invitee can accept and access their role.' },
      { id: 'onboard-org-created', title: 'Organisation + slug provisioned correctly', detail: 'New tenant gets a slug and the subdomain resolves to their site.' },
      { id: 'onboard-company-info', title: 'Company info can be completed', detail: 'Name, contact, address and branding save and render on the public site.' },
      { id: 'onboard-getting-started', title: 'Getting-started checklist guides setup', detail: 'Dashboard checklist reflects real progress and hides/dismisses correctly.' },
    ],
  },
  {
    id: 'journeys',
    title: 'Tenant & Customer Journeys',
    description: 'The public-facing experience on a tenant site.',
    icon: Users,
    scenarios: [
      { id: 'journey-public-home', title: 'Tenant homepage renders from DB content', detail: 'Branding, services and copy reflect the tenant — not the demo/fallback.' },
      { id: 'journey-services', title: 'Services page lists live services', detail: 'Public services come from the database and match admin configuration.' },
      { id: 'journey-about', title: 'About page renders tenant content', detail: 'About copy/images load from the tenant record.' },
      { id: 'journey-contact', title: 'Contact / enquiry form submits', detail: 'Submitting an enquiry shows confirmation and creates an enquiry record for the org.' },
      { id: 'journey-customer-dash', title: 'Customer dashboard via magic link', detail: 'Customer magic link opens their dashboard showing their bookings only.' },
      { id: 'journey-subdomain', title: 'Subdomain routing resolves correctly', detail: 'tenant.thepolishedversion.com loads the right tenant; unknown subdomains redirect to apex.' },
      { id: 'journey-footer-legal', title: 'Footer legal links work', detail: 'Privacy, terms and cookie links open the correct pages.' },
    ],
  },
  {
    id: 'services',
    title: 'Services & Availability',
    description: 'Service catalogue, pricing, durations and operating hours.',
    icon: Wrench,
    scenarios: [
      { id: 'svc-crud', title: 'Create, edit and remove a service', detail: 'Changes persist and reflect on the public services page.' },
      { id: 'svc-pricing', title: 'Pricing displays in the correct currency', detail: 'Jobs & pricing shows the org currency and formats money correctly.' },
      { id: 'svc-duration', title: 'Service duration drives booking slots', detail: 'Longer services consume the right number of slots; no overlap is offered.' },
      { id: 'svc-hours', title: 'Operational hours limit availability', detail: 'Bookable times respect operating hours and closed days.' },
      { id: 'svc-area', title: 'Service area / radius is enforced', detail: 'Out-of-area addresses are handled per configuration.' },
      { id: 'svc-timezone', title: 'Availability respects tenant timezone', detail: 'Slots are computed and displayed in the tenant timezone, not the viewer’s.' },
    ],
  },
  {
    id: 'booking',
    title: 'Booking Lifecycle',
    description: 'From request through confirmation, reschedule, completion and cancellation.',
    icon: CalendarCheck,
    scenarios: [
      { id: 'book-create', title: 'Customer can create a booking', detail: 'Anon/customer can book an available slot and receives confirmation.' },
      { id: 'book-conflict', title: 'Double-booking is prevented', detail: 'A slot taken by one booking is no longer offered; conflicts are detected server-side.' },
      { id: 'book-confirm', title: 'Owner can confirm a request', detail: 'New request can be accepted; status updates and customer is notified.' },
      { id: 'book-reschedule', title: 'Reschedule works for owner and customer', detail: 'Moving a booking updates the slot and notifies the other party.' },
      { id: 'book-cancel', title: 'Cancellation works and frees the slot', detail: 'Cancelling updates status, applies policy, and re-opens availability.' },
      { id: 'book-complete', title: 'Booking can be marked complete', detail: 'Completion updates dashboard metrics and enables review capture.' },
      { id: 'book-policy', title: 'Cancellation policy is visible to customer', detail: 'Policy text shows during booking and on the customer dashboard.' },
    ],
  },
  {
    id: 'payments',
    title: 'Payments & Stripe Readiness',
    description: 'Stripe connection, checkout, payment links, invoices and refunds.',
    icon: CreditCard,
    scenarios: [
      { id: 'pay-toggle', title: 'Accept-card-payments toggle works', detail: 'Enabling online payments in admin reflects on checkout; disabling hides it.' },
      { id: 'pay-checkout', title: 'Stripe checkout session is created', detail: 'A checkout session opens with the correct amount and currency.' },
      { id: 'pay-link', title: 'Stripe payment link generates and pays', detail: 'A payment link can be issued and a test payment completes.' },
      { id: 'pay-confirm-email', title: 'Payment confirmation email is sent', detail: 'Successful Stripe payment triggers a confirmation email.' },
      { id: 'pay-invoice-send', title: 'Invoice can be created and sent', detail: 'Invoice generates, sends to the customer, and appears in their record.' },
      { id: 'pay-refund', title: 'Refund processes correctly', detail: 'A refund updates invoice status and notifies the customer.' },
      { id: 'pay-policy', title: 'Payment policy displays correctly', detail: 'Deposit/payment policy shows on booking and admin matches.' },
      { id: 'pay-billing', title: 'Subscription / billing admin reflects plan', detail: 'Billing page shows the tenant’s plan and trial state accurately.' },
    ],
  },
  {
    id: 'emails',
    title: 'Emails, Magic Links & Reminders',
    description: 'Transactional email delivery, magic-link auth and reminder timing.',
    icon: Mail,
    scenarios: [
      { id: 'email-branding', title: 'Emails use tenant branding', detail: 'Outbound emails carry the tenant name/branding, not the platform default.' },
      { id: 'email-magic-customer', title: 'Customer magic link signs in', detail: 'Magic link opens the customer dashboard; expired links are handled.' },
      { id: 'email-magic-resend', title: 'Magic link can be resent', detail: 'Resend works and previous links behave as expected.' },
      { id: 'email-owner-enquiry', title: 'Owner is notified of new enquiry', detail: 'Submitting an enquiry emails the owner.' },
      { id: 'email-enquiry-accepted', title: 'Customer notified when enquiry accepted', detail: 'Accepting an enquiry triggers a customer notification.' },
      { id: 'email-job-assignment', title: 'Team member notified of job assignment', detail: 'Assigning a job emails the assigned team member.' },
      { id: 'email-reminders', title: 'Booking reminders send on schedule', detail: 'Upcoming-booking reminders fire at the configured time.' },
    ],
  },
  {
    id: 'admin-ops',
    title: 'Admin Operations',
    description: 'Day-to-day running of a tenant from the admin panel.',
    icon: UserCog,
    scenarios: [
      { id: 'ops-dashboard', title: 'Dashboard metrics are accurate', detail: 'Today/tomorrow jobs, revenue and counts match underlying data.' },
      { id: 'ops-customers', title: 'Customers list and detail work', detail: 'Search, view and edit customer records scoped to the org.' },
      { id: 'ops-enquiries', title: 'Enquiries can be triaged', detail: 'Enquiries can be viewed, replied to and converted.' },
      { id: 'ops-schedule', title: 'Schedule / calendar reflects bookings', detail: 'Calendar shows correct jobs, assignments and times.' },
      { id: 'ops-team', title: 'Team management works', detail: 'Add, edit role and remove team members; role enum is respected.' },
      { id: 'ops-theme', title: 'Theme and page builder save', detail: 'Theme/page edits persist and render on the public site.' },
      { id: 'ops-reviews', title: 'Reviews capture and moderation', detail: 'Reviews can be added/approved and show publicly when intended.' },
    ],
  },
  {
    id: 'security',
    title: 'Security, RLS & Access Isolation',
    description: 'Cross-org isolation, public endpoint hardening and least privilege.',
    icon: ShieldCheck,
    scenarios: [
      { id: 'sec-cross-org', title: 'Cross-org data isolation holds', detail: 'Org A cannot read or write Org B data via UI or API; RLS blocks it.' },
      { id: 'sec-list-scoping', title: 'Admin lists are scoped to the org', detail: 'Customers/invoices/bookings queries filter by the current org_id.' },
      { id: 'sec-platform-only', title: 'Platform RPCs require super_admin', detail: 'Platform-wide RPCs reject non-platform-admin callers.' },
      { id: 'sec-public-hardening', title: 'Public edge functions are hardened', detail: 'Public endpoints validate input and do not leak other tenants’ data.' },
      { id: 'sec-places', title: 'Google Places proxy is restricted', detail: 'The Places integration cannot be abused as an open proxy.' },
      { id: 'sec-impersonation', title: 'Impersonation is auditable and banner-visible', detail: 'Platform admin impersonation shows a banner and is logged in the audit trail.' },
      { id: 'sec-suspended', title: 'Suspended tenant is blocked publicly', detail: 'A suspended tenant’s public site shows the unavailable notice.' },
    ],
  },
  {
    id: 'mobile',
    title: 'Mobile & Responsive',
    description: 'Usability across phone, tablet and desktop in light and dark mode.',
    icon: Smartphone,
    scenarios: [
      { id: 'mob-admin-nav', title: 'Admin nav works on mobile', detail: 'Hamburger/sheet nav opens and all links are reachable on a phone.' },
      { id: 'mob-booking', title: 'Booking flow is usable on mobile', detail: 'Date/slot selection and forms work on small screens.' },
      { id: 'mob-public', title: 'Public pages are responsive', detail: 'Homepage, services and contact reflow without overflow.' },
      { id: 'mob-darkmode', title: 'Dark and light mode both render correctly', detail: 'No unreadable contrast; theme toggle works.' },
      { id: 'mob-tablet', title: 'Tablet breakpoints look intentional', detail: 'Mid-size layouts are not broken between mobile and desktop.' },
    ],
  },
  {
    id: 'errors',
    title: 'Error States',
    description: 'Graceful handling of failures, empty data and bad input.',
    icon: TriangleAlert,
    scenarios: [
      { id: 'err-boundary', title: 'Error boundary catches render failures', detail: 'A thrown error shows a friendly fallback, not a white screen.' },
      { id: 'err-network', title: 'Failed data loads show a clear message', detail: 'API/network errors render a retry/error state, not a spinner forever.' },
      { id: 'err-empty', title: 'Empty states are friendly', detail: 'No-data lists (no invoices/customers) show helpful empty copy.' },
      { id: 'err-validation', title: 'Form validation gives clear feedback', detail: 'Zod-validated forms surface field errors before submit.' },
      { id: 'err-404', title: 'Unknown routes show NotFound', detail: 'A bad URL renders the 404 page rather than crashing.' },
      { id: 'err-permission', title: 'Permission-denied is handled', detail: 'Accessing something out of scope shows an access-required state.' },
    ],
  },
  {
    id: 'deploy',
    title: 'Deployment & Smoke Checks',
    description: 'Post-deploy verification on the live environment.',
    icon: Rocket,
    scenarios: [
      { id: 'deploy-build', title: 'Production build succeeds', detail: 'bun run build completes with no errors.' },
      { id: 'deploy-lint', title: 'Lint passes', detail: 'bun run lint reports no new errors.' },
      { id: 'deploy-e2e', title: 'E2E suite passes', detail: 'Playwright suite is green for the release.' },
      { id: 'deploy-vercel', title: 'Vercel deploy is live', detail: 'main auto-deploys and the production URL serves the new build.' },
      { id: 'deploy-functions', title: 'Edge functions are deployed', detail: 'Required Supabase functions are deployed and responding.' },
      { id: 'deploy-smoke', title: 'Smoke test the critical path', detail: 'Sign in → create booking → payment → email all work on production.' },
      { id: 'deploy-rollback', title: 'Rollback plan is known', detail: 'A previous good deploy can be restored quickly if needed.' },
    ],
  },
];
